<template>
    <div class="page">
        <div class="page-header">
            <div>
                <h2 class="page-title">{{ t('roles') }}</h2>
                <p class="page-subtitle">{{ t('roles_page_subtitle') }}</p>
            </div>
        </div>

        <div class="card">
            <div class="datatable-toolbar">
                <div class="toolbar-left">
                    <select v-model.number="perPage" class="length-select" :disabled="loading">
                        <option :value="10">10</option>
                        <option :value="20">20</option>
                        <option :value="50">50</option>
                    </select>

                    <select v-model="statusFilter" class="status-select" :disabled="loading">
                        <option value="all">{{ t('all') }}</option>
                        <option value="active">{{ t('active') }}</option>
                        <option value="inactive">{{ t('inactive') }}</option>
                    </select>

                    <button type="button" class="tool-btn tool-btn-icon" @click="toggleFilters" :disabled="loading">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                            <path d="M2.5 4.16667H17.5M5.83333 10H14.1667M8.33333 15.8333H11.6667" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>

                    <div class="columns-wrap">
                        <button type="button" class="tool-btn tool-btn-light" @click="toggleColumns" :disabled="loading">COLUMNS</button>
                        <div v-if="showColumns" class="columns-dropdown">
                            <label v-for="c in columns" :key="c.key" class="columns-item">
                                <input type="checkbox" v-model="visibleColumns[c.key]" :disabled="c.locked">
                                <span>{{ t(c.labelKey) }}</span>
                            </label>
                        </div>
                    </div>

                    <button type="button" class="tool-btn tool-btn-primary" @click="loadRoles" :disabled="loading">{{ t('refresh') }}</button>
                    <button type="button" class="tool-btn tool-btn-light" @click="clearFilters" :disabled="!hasAnyFilter">{{ t('clear_filters') }}</button>
                    <button
                        v-if="permissionsLoaded && canCreateRoles"
                        type="button"
                        class="tool-btn tool-btn-success"
                        @click="openCreateModal"
                        :disabled="loading"
                    >
                        {{ t('create_role') }}
                    </button>
                </div>

                <div class="toolbar-right">
                    <div class="search-box">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <input v-model="searchQuery" type="text" class="search-input" :placeholder="t('search_roles') + '...'" @input="debouncedSearch" :disabled="loading">
                    </div>
                </div>
            </div>

            <div class="table-container">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th v-if="visibleColumns.name">
                                <input v-if="showFilters" v-model="colFilters.name" type="text" class="th-input" :placeholder="t('roles')">
                                <span v-else>{{ t('roles') }}</span>
                            </th>
                            <th v-if="visibleColumns.slug">
                                <input v-if="showFilters" v-model="colFilters.slug" type="text" class="th-input" :placeholder="t('slug')">
                                <span v-else>{{ t('slug') }}</span>
                            </th>
                            <th v-if="visibleColumns.status">
                                <select v-if="showFilters" v-model="colFilters.status" class="th-select">
                                    <option value="">{{ t('status') }}</option>
                                    <option value="active">{{ t('active') }}</option>
                                    <option value="inactive">{{ t('inactive') }}</option>
                                </select>
                                <span v-else>{{ t('status') }}</span>
                            </th>
                            <th v-if="visibleColumns.users">
                                <input v-if="showFilters" v-model="colFilters.users" type="text" class="th-input" :placeholder="t('users')">
                                <span v-else>{{ t('users') }}</span>
                            </th>
                            <th v-if="visibleColumns.permissions">
                                <input v-if="showFilters" v-model="colFilters.permissions" type="text" class="th-input" :placeholder="t('permissions')">
                                <span v-else>{{ t('permissions') }}</span>
                            </th>
                            <th v-if="visibleColumns.actions">{{ t('actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td :colspan="visibleColumnCount" style="padding: 10px;">
                                <div class="skeleton-wrap">
                                    <div v-for="n in 6" :key="n" class="skeleton-row"></div>
                                </div>
                            </td>
                        </tr>
                        <tr v-for="role in filteredRoles" v-if="!loading" :key="role.id">
                            <td v-if="visibleColumns.name">
                                <div class="user-info">
                                    <div class="user-avatar-small">{{ initials(role.name) }}</div>
                                    <span class="user-name">{{ role.name }}</span>
                                </div>
                            </td>
                            <td v-if="visibleColumns.slug">{{ role.slug }}</td>
                            <td v-if="visibleColumns.status">
                                <span :class="['tag', role.is_active ? 'tag-primary' : 'tag-gray']">
                                    {{ role.is_active ? t('active') : t('inactive') }}
                                </span>
                            </td>
                            <td v-if="visibleColumns.users">{{ role.users_count ?? 0 }}</td>
                            <td v-if="visibleColumns.permissions">{{ role.permissions_count ?? 0 }}</td>
                            <td v-if="visibleColumns.actions">
                                <div class="action-buttons">
                                    <button
                                        v-if="permissionsLoaded && canEditRoles"
                                        @click="openEditModal(role)"
                                        class="icon-btn icon-btn-primary"
                                        :title="t('edit')"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                            <path d="M11.6667 2.5L15.8333 6.66667M13.3333 1.66667L17.5 5.83333L10.8333 12.5H6.66667V8.33333L13.3333 1.66667Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                            <path d="M2.5 18.3333H17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                                        </svg>
                                    </button>

                                    <button
                                        v-if="permissionsLoaded && canDeleteRoles"
                                        @click="deleteRole(role)"
                                        class="icon-btn icon-btn-danger"
                                        :title="t('delete')"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                            <path d="M15.8333 5V16.6667C15.8333 17.5872 15.0872 18.3333 14.1667 18.3333H5.83333C4.91286 18.3333 4.16667 17.5872 4.16667 16.6667V5M12.5 5V3.33333C12.5 2.41286 11.7538 1.66667 10.8333 1.66667H9.16667C8.24619 1.66667 7.5 2.41286 7.5 3.33333V5M2.5 5H17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </button>

                                    <span v-if="permissionsLoaded && !canEditRoles && !canDeleteRoles" class="no-access-text">{{ t('view_only') }}</span>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="!loading && filteredRoles.length === 0">
                            <td :colspan="visibleColumnCount" class="empty">{{ t('no_data') }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="pager">
                <div class="pager-left">{{ showingText }}</div>
                <div class="pager-right">
                    <button class="pager-btn" @click="goFirst" :disabled="loading || page <= 1">{{ t('first') }}</button>
                    <button class="pager-btn" @click="prevPage" :disabled="loading || page <= 1">{{ t('previous') }}</button>
                    <button v-for="p in pageButtons" :key="p" class="pager-num" :class="{ active: p === page }" @click="goPage(p)" :disabled="loading">
                        {{ p }}
                    </button>
                    <button class="pager-btn" @click="nextPage" :disabled="loading || page >= totalPages">{{ t('next') }}</button>
                    <button class="pager-btn" @click="goLast" :disabled="loading || page >= totalPages">{{ t('last') }}</button>
                </div>
            </div>
        </div>

        <div v-if="showModal" class="modal-overlay" @click="closeModal">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>{{ editingRole ? t('edit') + ' ' + t('roles') : t('create_role') }}</h3>
                    <button @click="closeModal" class="close-btn">
                        <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
                            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>
                <form @submit.prevent="saveRole" class="modal-body">
                    <div class="form-group">
                        <label>{{ t('name') }}</label>
                        <input v-model="form.name" type="text" required class="form-input">
                    </div>
                    <div class="form-group">
                        <label>{{ t('slug') }}</label>
                        <input v-model="form.slug" type="text" class="form-input" :placeholder="t('auto_generate_if_empty')">
                    </div>
                    <div class="form-group">
                        <label>{{ t('description') }}</label>
                        <textarea v-model="form.description" class="form-input" rows="3"></textarea>
                    </div>
                    <div class="form-group">
                        <label class="checkbox-label">
                            <input type="checkbox" v-model="form.is_active">
                            <span>{{ t('active') }}</span>
                        </label>
                    </div>

                    <div class="permissions-section">
                        <div class="permissions-section-header">
                            <div class="permissions-title">{{ t('permissions') }}</div>
                            <div class="permissions-actions">
                                <button type="button" class="btn btn-secondary btn-sm" @click="selectAllVisible">{{ t('select_visible') }}</button>
                                <button type="button" class="btn btn-secondary btn-sm" @click="clearAll">{{ t('clear') }}</button>
                            </div>
                        </div>

                        <div class="table-header permissions-toolbar">
                            <div class="search-box">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                <input v-model="permissionSearch" type="text" class="search-input" :placeholder="t('search_permissions') + '...'">
                            </div>
                        </div>

                        <div v-if="groupedPermissions.length" class="permissions-grid">
                            <div v-for="group in groupedPermissions" :key="group.name" class="permissions-group">
                                <div class="permissions-group-header">
                                    <div class="permissions-group-title">{{ group.name }}</div>
                                    <div class="permissions-group-actions">
                                        <button type="button" class="btn btn-secondary btn-xs" @click="selectGroup(group.items)">{{ t('all') }}</button>
                                        <button type="button" class="btn btn-secondary btn-xs" @click="clearGroup(group.items)">{{ t('clear') }}</button>
                                    </div>
                                </div>
                                <label v-for="perm in group.items" :key="perm.id" class="checkbox-label">
                                    <input type="checkbox" :value="perm.id" v-model="selectedPermissionIds">
                                    <span>{{ perm.name }} <small class="text-muted">({{ perm.slug }})</small></span>
                                </label>
                            </div>
                        </div>

                        <div v-else class="empty-state">{{ t('no_permissions_found') }}</div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="closeModal" class="btn btn-secondary">{{ t('cancel') }}</button>
                        <button type="submit" class="btn btn-primary">{{ t('save') }}</button>
                    </div>
                </form>
            </div>
        </div>

        <Teleport to="body">
            <div class="toast-container">
                <TransitionGroup name="toast" tag="div">
                    <div v-for="toast in toasts" :key="toast.id" :class="['toast', `toast-${toast.type}`]">
                        <div class="toast-content">
                            <p class="toast-message">{{ toast.message }}</p>
                        </div>
                        <button @click="removeToast(toast.id)" class="toast-close">
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </TransitionGroup>
            </div>
        </Teleport>
    </div>
</template>

<script>
import { computed, inject, onMounted, ref, Teleport, TransitionGroup, watch } from 'vue';
import axios from 'axios';

export default {
    name: 'RolesPage',
    setup() {
        const adminT = inject('adminT', (k) => k);
        const t = (key) => adminT(key);

        const roles = ref([]);
        const permissions = ref([]);
        const searchQuery = ref('');
        const loading = ref(true);
        const page = ref(1);
        const perPage = ref(20);
        const statusFilter = ref('all');
        const pagination = ref({ page: 1, per_page: 20, total: 0 });
        const showFilters = ref(true);
        const showColumns = ref(false);
        const columns = ref([
            { key: 'name', labelKey: 'roles', locked: true },
            { key: 'slug', labelKey: 'slug', locked: false },
            { key: 'status', labelKey: 'status', locked: false },
            { key: 'users', labelKey: 'users', locked: false },
            { key: 'permissions', labelKey: 'permissions', locked: false },
            { key: 'actions', labelKey: 'actions', locked: true },
        ]);
        const visibleColumns = ref({
            name: true,
            slug: true,
            status: true,
            users: true,
            permissions: true,
            actions: true,
        });
        const colFilters = ref({
            name: '',
            slug: '',
            status: '',
            users: '',
            permissions: '',
        });
        let searchTimer = null;
        const showModal = ref(false);
        const editingRole = ref(null);
        const form = ref({ name: '', slug: '', description: '', is_active: true });
        const toasts = ref([]);
        const permissionsLoaded = ref(false);
        const userPermissions = ref([]);
        const selectedPermissionIds = ref([]);
        const permissionSearch = ref('');

        const showNotification = (message, type = 'success') => {
            const id = Date.now() + Math.random();
            toasts.value.push({ id, message, type });
            setTimeout(() => removeToast(id), 4000);
        };

        const removeToast = (id) => {
            const idx = toasts.value.findIndex(t => t.id === id);
            if (idx > -1) toasts.value.splice(idx, 1);
        };

        const hasAnyPermission = (patterns) => {
            if (!permissionsLoaded.value) return false;
            const perms = userPermissions.value || [];
            if (perms.includes('*')) return true;
            const list = Array.isArray(patterns) ? patterns : [patterns];
            return perms.some(p => list.some(x => (p || '').toLowerCase() === (x || '').toLowerCase()));
        };

        const canCreateRoles = computed(() => hasAnyPermission(['create-roles']));
        const canEditRoles = computed(() => hasAnyPermission(['edit-roles', 'edite-roles']));
        const canDeleteRoles = computed(() => hasAnyPermission(['delete-roles']));

        const filteredRoles = computed(() => {
            const list = Array.isArray(roles.value) ? roles.value : [];
            const f = colFilters.value || {};
            const nameQ = String(f.name || '').trim().toLowerCase();
            const slugQ = String(f.slug || '').trim().toLowerCase();
            const statusQ = String(f.status || '').trim().toLowerCase();
            const usersQ = String(f.users || '').trim().toLowerCase();
            const permsQ = String(f.permissions || '').trim().toLowerCase();

            if (!nameQ && !slugQ && !statusQ && !usersQ && !permsQ) return list;

            return list.filter((r) => {
                const name = String(r?.name || '').toLowerCase();
                const slug = String(r?.slug || '').toLowerCase();
                const isActive = !!r?.is_active;
                const users = String(r?.users_count ?? '').toLowerCase();
                const perms = String(r?.permissions_count ?? '').toLowerCase();

                if (nameQ && !name.includes(nameQ)) return false;
                if (slugQ && !slug.includes(slugQ)) return false;
                if (statusQ === 'active' && !isActive) return false;
                if (statusQ === 'inactive' && isActive) return false;
                if (usersQ && !users.includes(usersQ)) return false;
                if (permsQ && !perms.includes(permsQ)) return false;
                return true;
            });
        });

        const visibleColumnCount = computed(() => {
            const v = visibleColumns.value || {};
            return Object.values(v).filter(Boolean).length || 1;
        });

        const hasAnyFilter = computed(() => {
            if ((searchQuery.value || '').trim() !== '') return true;
            if ((statusFilter.value || 'all') !== 'all') return true;
            const f = colFilters.value || {};
            return Object.values(f).some((x) => String(x || '').trim() !== '');
        });

        const toggleFilters = () => {
            showFilters.value = !showFilters.value;
        };

        const toggleColumns = () => {
            showColumns.value = !showColumns.value;
        };

        const clearFilters = async () => {
            searchQuery.value = '';
            statusFilter.value = 'all';
            colFilters.value = { name: '', slug: '', status: '', users: '', permissions: '' };
            page.value = 1;
            await loadRoles();
        };

        const visiblePermissions = computed(() => {
            if (!permissionSearch.value) return permissions.value;
            const q = permissionSearch.value.toLowerCase();
            return permissions.value.filter(p =>
                (p.name || '').toLowerCase().includes(q) ||
                (p.slug || '').toLowerCase().includes(q) ||
                (p.group || '').toLowerCase().includes(q)
            );
        });

        const groupedPermissions = computed(() => {
            const groups = new Map();
            visiblePermissions.value.forEach(p => {
                const g = p.group || 'General';
                if (!groups.has(g)) groups.set(g, []);
                groups.get(g).push(p);
            });
            return Array.from(groups.entries()).map(([name, items]) => ({ name, items }));
        });

        const loadUserPermissions = async () => {
            try {
                const res = await axios.get('/admin/api/user-permissions');
                userPermissions.value = Array.isArray(res.data.permissions) ? res.data.permissions : [];
            } catch (e) {
                userPermissions.value = [];
            } finally {
                permissionsLoaded.value = true;
            }
        };

        const loadRoles = async () => {
            try {
                loading.value = true;
                const res = await axios.get('/admin/api/roles', {
                    params: {
                        page: page.value,
                        per_page: perPage.value,
                        q: searchQuery.value || '',
                        status: statusFilter.value,
                    },
                });

                const payload = res.data || {};
                const items = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);

                if (Array.isArray(payload.data)) {
                    const nextPageValue = Number(payload.current_page || page.value || 1);
                    const nextPerPage = Number(payload.per_page || perPage.value || 20);
                    const nextTotal = Number(payload.total || 0);
                    pagination.value = { page: nextPageValue, per_page: nextPerPage, total: nextTotal };
                    page.value = nextPageValue;
                    perPage.value = nextPerPage;
                } else {
                    pagination.value = { page: 1, per_page: items.length, total: items.length };
                    page.value = 1;
                }

                roles.value = items;
            } catch (e) {
                showNotification(e?.response?.data?.message || t('failed_to_load_roles') || 'Failed to load roles', 'error');
            } finally {
                loading.value = false;
            }
        };

        const totalPages = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(perPage.value || pagination.value?.per_page || 20);
            return Math.max(1, Math.ceil(total / Math.max(1, size)));
        });

        const pageButtons = computed(() => {
            const current = Math.min(Math.max(1, page.value || 1), totalPages.value);
            const total = totalPages.value;
            const max = 5;
            let start = Math.max(1, current - Math.floor(max / 2));
            let end = start + max - 1;
            if (end > total) {
                end = total;
                start = Math.max(1, end - max + 1);
            }
            const out = [];
            for (let p = start; p <= end; p++) out.push(p);
            return out;
        });

        const showingText = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(perPage.value || pagination.value?.per_page || 20);
            const current = Math.min(Math.max(1, page.value || 1), totalPages.value);
            const from = total === 0 ? 0 : (current - 1) * size + 1;
            const to = total === 0 ? 0 : Math.min(current * size, total);
            const tpl = t('showing_entries');
            const base = tpl && tpl !== 'showing_entries' && tpl.includes('{from}')
                ? tpl.replace('{from}', String(from)).replace('{to}', String(to)).replace('{total}', String(total))
                : `Showing ${from} to ${to} of ${total} entries`;

            if (!hasAnyFilter.value) return base;
            const filteredTpl = t('filtered');
            const suffix = filteredTpl && filteredTpl !== 'filtered' && filteredTpl.includes('{count}')
                ? filteredTpl.replace('{count}', String(filteredRoles.value.length))
                : `(filtered: ${filteredRoles.value.length})`;
            return `${base} ${suffix}`;
        });

        const goPage = async (p) => {
            const next = Math.min(Math.max(1, Number(p || 1)), totalPages.value);
            if (next === page.value) return;
            page.value = next;
            await loadRoles();
        };

        const nextPage = async () => {
            if (page.value >= totalPages.value) return;
            page.value += 1;
            await loadRoles();
        };

        const prevPage = async () => {
            if (page.value <= 1) return;
            page.value -= 1;
            await loadRoles();
        };

        const goFirst = async () => {
            if (page.value <= 1) return;
            page.value = 1;
            await loadRoles();
        };

        const goLast = async () => {
            if (page.value >= totalPages.value) return;
            page.value = totalPages.value;
            await loadRoles();
        };

        const debouncedSearch = () => {
            if (searchTimer) clearTimeout(searchTimer);
            searchTimer = setTimeout(() => {
                page.value = 1;
                loadRoles();
            }, 350);
        };

        watch(perPage, async () => {
            page.value = 1;
            await loadRoles();
        });

        watch(statusFilter, async () => {
            page.value = 1;
            await loadRoles();
        });

        const loadPermissions = async () => {
            const res = await axios.get('/admin/api/permissions', { params: { per_page: 500 } });
            const payload = res.data || {};
            permissions.value = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);
        };

        const ensurePermissionsLoaded = async () => {
            if (permissions.value.length) return;
            await loadPermissions();
        };

        const loadRolePermissionIds = async (roleId) => {
            const res = await axios.get(`/admin/api/roles/${roleId}/permissions`);
            return Array.isArray(res.data.permission_ids) ? res.data.permission_ids : [];
        };

        const openCreateModal = async () => {
            editingRole.value = null;
            form.value = { name: '', slug: '', description: '', is_active: true };
            permissionSearch.value = '';
            selectedPermissionIds.value = [];
            await ensurePermissionsLoaded();
            showModal.value = true;
        };

        const openEditModal = async (role) => {
            editingRole.value = role;
            form.value = {
                name: role.name || '',
                slug: role.slug || '',
                description: role.description || '',
                is_active: !!role.is_active,
            };
            permissionSearch.value = '';
            await ensurePermissionsLoaded();
            selectedPermissionIds.value = await loadRolePermissionIds(role.id);
            showModal.value = true;
        };

        const closeModal = () => {
            showModal.value = false;
            editingRole.value = null;
            selectedPermissionIds.value = [];
            permissionSearch.value = '';
        };

        const saveRole = async () => {
            try {
                if (!selectedPermissionIds.value.length) {
                    showNotification(t('select_at_least_one_permission'), 'error');
                    return;
                }

                let roleId = editingRole.value?.id;
                if (editingRole.value) {
                    const res = await axios.put(`/admin/api/roles/${editingRole.value.id}`, form.value);
                    roleId = res.data?.id ?? roleId;
                } else {
                    const res = await axios.post('/admin/api/roles', form.value);
                    roleId = res.data?.id;
                }

                if (!roleId) {
                    showNotification(t('role_saved_permission_update_failed'), 'error');
                    return;
                }

                await axios.put(`/admin/api/roles/${roleId}/permissions`, {
                    permission_ids: selectedPermissionIds.value,
                });

                await loadRoles();
                closeModal();
                showNotification(t('role_saved_success'), 'success');
            } catch (e) {
                showNotification(e?.response?.data?.message || t('failed_to_save_role'), 'error');
            }
        };

        const deleteRole = async (role) => {
            if (!confirm(`${t('delete')} ${t('roles')} "${role.name}"?`)) return;
            try {
                await axios.delete(`/admin/api/roles/${role.id}`);
                await loadRoles();
                showNotification(t('role_deleted'), 'success');
            } catch (e) {
                showNotification(e?.response?.data?.message || t('failed_to_delete_role'), 'error');
            }
        };

        const selectAllVisible = () => {
            const visibleIds = visiblePermissions.value.map(p => p.id);
            const set = new Set(selectedPermissionIds.value);
            visibleIds.forEach(id => set.add(id));
            selectedPermissionIds.value = Array.from(set);
        };

        const selectGroup = (items) => {
            const set = new Set(selectedPermissionIds.value);
            (items || []).forEach(p => set.add(p.id));
            selectedPermissionIds.value = Array.from(set);
        };

        const clearGroup = (items) => {
            const removeIds = new Set((items || []).map(p => p.id));
            selectedPermissionIds.value = selectedPermissionIds.value.filter(id => !removeIds.has(id));
        };

        const clearAll = () => {
            selectedPermissionIds.value = [];
        };

        const initials = (name) => {
            const n = (name || '').trim();
            if (!n) return 'R';
            return n.split(' ').map(x => x[0]).join('').toUpperCase().slice(0, 2);
        };

        onMounted(async () => {
            await loadUserPermissions();
            await loadRoles();
        });

        return {
            t,
            roles,
            searchQuery,
            loading,
            page,
            perPage,
            statusFilter,
            pagination,
            filteredRoles,
            showFilters,
            toggleFilters,
            showColumns,
            toggleColumns,
            columns,
            visibleColumns,
            visibleColumnCount,
            colFilters,
            hasAnyFilter,
            clearFilters,
            totalPages,
            pageButtons,
            showingText,
            goPage,
            nextPage,
            prevPage,
            goFirst,
            goLast,
            debouncedSearch,
            loadRoles,
            showModal,
            editingRole,
            form,
            permissionsLoaded,
            canCreateRoles,
            canEditRoles,
            canDeleteRoles,
            openCreateModal,
            openEditModal,
            closeModal,
            saveRole,
            deleteRole,
            permissions,
            groupedPermissions,
            selectedPermissionIds,
            permissionSearch,
            selectAllVisible,
            selectGroup,
            clearGroup,
            clearAll,
            toasts,
            removeToast,
            initials,
        };
    },
};
</script>

<style scoped>
.page {
    display: flex;
    flex-direction: column;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-title {
    font-size: 28px;
    font-weight: 900;
    color: #0f172a;
    margin-bottom: 4px;
}

.page-subtitle {
    font-size: 14px;
    color: #64748b;
}

.btn {
    padding: 10px 16px;
    border-radius: 10px;
    border: 1px solid transparent;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s;
    font-size: 14px;
}

.btn-primary {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.card {
    margin-top: 18px;
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.datatable-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 12px;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
}

.length-select,
.status-select {
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #ffffff;
    padding: 0 10px;
    font-weight: 800;
    font-size: 12px;
}

.tool-btn {
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    padding: 0 12px;
    font-weight: 900;
    font-size: 12px;
    cursor: pointer;
}

.tool-btn-icon {
    width: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.tool-btn-light {
    background: #f1f5f9;
    color: #0f172a;
}

.tool-btn-primary {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.tool-btn-success {
    background: #16a34a;
    color: #ffffff;
    border-color: #16a34a;
}

.tool-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.columns-wrap {
    position: relative;
}

.columns-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    min-width: 200px;
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.12);
    border-radius: 12px;
    padding: 10px 10px;
    box-shadow: 0 18px 34px rgba(15, 23, 42, 0.12);
    z-index: 40;
}

.columns-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 8px;
    border-radius: 10px;
    cursor: pointer;
    font-weight: 800;
    color: #0f172a;
    font-size: 12px;
}

.columns-item:hover {
    background: rgba(241, 245, 249, 0.8);
}

.search-box {
    position: relative;
    max-width: 520px;
    width: 100%;
}

.search-box svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
}

.search-input {
    width: 100%;
    height: 36px;
    border-radius: 8px;
    border: 2px solid #2563eb;
    padding: 0 12px 0 42px;
    font-size: 13px;
    outline: none;
}

.table-container {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    table-layout: fixed;
}

.data-table thead {
    background: #0b2c3d;
}

.data-table th {
    padding: 10px 8px;
    text-align: center;
    font-size: 12px;
    font-weight: 900;
    color: #ffffff;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    vertical-align: top;
    white-space: nowrap;
}

.th-input,
.th-select {
    width: 100%;
    height: 30px;
    border-radius: 6px;
    border: 1px solid rgba(148, 163, 184, 0.9);
    background: #ffffff;
    padding: 0 8px;
    font-weight: 700;
    font-size: 12px;
    outline: none;
}

.data-table td {
    padding: 9px 8px;
    border-top: 1px solid #e2e8f0;
    color: #0f172a;
    font-size: 13px;
    vertical-align: top;
    background: #ffffff;
}

.data-table tbody tr:nth-child(even) td {
    background: rgba(241, 245, 249, 0.55);
}

.data-table tbody tr:hover td {
    background: rgba(226, 232, 240, 0.55);
}

.user-info {
    display: flex;
    align-items: center;
    gap: 12px;
}

.user-avatar-small {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
}

.user-name {
    font-weight: 600;
    color: #1a202c;
}

.tag {
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
}

.tag-primary {
    background: #e6f0ff;
    color: #4A90E2;
}

.tag-gray {
    background: #edf2f7;
    color: #718096;
}

.action-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
}

.icon-btn {
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.icon-btn-primary {
    background: #e6f0ff;
    color: #4A90E2;
}

.icon-btn-danger {
    background: #fed7d7;
    color: #e53e3e;
}

.no-access-text {
    color: #718096;
    font-size: 12px;
    font-style: italic;
    padding: 8px;
}

.empty {
    text-align: center;
    color: #64748b;
    font-weight: 800;
    padding: 16px;
}

.pager {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 2px 10px;
    flex-wrap: wrap;
}

.pager-left {
    color: #64748b;
    font-weight: 800;
    font-size: 12px;
}

.pager-right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.pager-btn {
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #f1f5f9;
    color: #0f172a;
    padding: 7px 10px;
    border-radius: 6px;
    font-weight: 800;
    cursor: pointer;
    min-width: 66px;
}

.pager-num {
    min-width: 44px;
    height: 32px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #f1f5f9;
    color: #0f172a;
    border-radius: 6px;
    font-weight: 900;
    cursor: pointer;
}

.pager-num.active {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.pager-btn:disabled,
.pager-num:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.skeleton-wrap {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.skeleton-row {
    height: 12px;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(226, 232, 240, 0.7), rgba(226, 232, 240, 0.25), rgba(226, 232, 240, 0.7));
    background-size: 200% 100%;
    animation: shimmer 1.2s infinite;
}

@keyframes shimmer {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 16px;
    width: 90%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
}

.modal-large {
    max-width: 920px;
}

.modal-header {
    padding: 24px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: #f7fafc;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #718096;
}

.modal-body {
    padding: 24px;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 10px;
}

.form-group {
    margin-bottom: 16px;
}

.form-group label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #2d3748;
}

.form-input {
    width: 100%;
    padding: 12px 16px;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    font-size: 14px;
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    font-size: 14px;
    color: #2d3748;
    margin-bottom: 10px;
}

.btn-sm {
    padding: 8px 12px;
    border-radius: 10px;
    font-size: 12px;
}

.btn-xs {
    padding: 6px 10px;
    border-radius: 10px;
    font-size: 11px;
}

.permissions-section {
    margin-top: 18px;
    padding-top: 18px;
    border-top: 1px solid #e2e8f0;
}

.permissions-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
}

.permissions-title {
    font-weight: 800;
    color: #1a202c;
}

.permissions-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.permissions-toolbar {
    margin-bottom: 14px;
}

.empty-state {
    padding: 14px;
    border: 1px dashed #e2e8f0;
    border-radius: 12px;
    color: #718096;
    font-weight: 600;
    text-align: center;
}

.permissions-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 16px;
}

.permissions-group {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 14px;
}

.permissions-group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 10px;
}

.permissions-group-title {
    font-weight: 700;
    color: #1a202c;
}

.permissions-group-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.text-muted {
    color: #718096;
}

.toast-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 2000;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.toast {
    background: #ffffff;
    border-radius: 12px;
    padding: 12px 14px;
    box-shadow: 0 8px 26px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-width: 280px;
    border-left: 4px solid #4A90E2;
}

.toast-error {
    border-left-color: #e53e3e;
}

.toast-message {
    margin: 0;
    font-size: 13px;
    color: #1a202c;
}

.toast-close {
    background: transparent;
    border: none;
    cursor: pointer;
    color: #718096;
}
</style>
